import React from "react";
import { Link } from "react-router-dom";
import type { Crumb } from "./PageMetaContext";
import { useLanguage } from "../ui/LanguageProvider";
import { useAuth } from "../auth/AuthContext";
import FeedbackWidget from "../feedback/FeedbackWidget";
import NotificationBell from "./NotificationBell";
import AnnouncementBanner from "./AnnouncementBanner";
import "./topNavStyle.css";

/**
 * 앱 셸(CRM · IT-CONSULTING) 공용 상단 컨텍스트 바.
 * 좌: 경로(crumbs, DESIGN-AI §1 — 페이지 이름은 페이지 제목에만) 또는 라우트 기반 페이지명(pageLabel, 옛 방식)
 * 우: 피드백 · (도움말=children) · 알림 · 한/영.
 * 알림은 상단 바에서 아래로 열려 하단 footer처럼 잘리지 않는다.
 * 한/영 토글은 상단 우측에 크게 둬 눈에 띈다(사이드바 소형 토글에서 이관).
 *
 * ★ 업데이트 알림 배너를 이 바 **위**(앱 영역 최상단)에 붙인다. 여기 한 곳이면
 *   CRM·IT-CONSULTING·설정·범용 페이지가 전부 덮이고, /home·?embedded=1·/pdf 는
 *   PageLayout 이 이 바를 아예 안 그리므로 제외 조건을 따로 쓸 필요가 없다.
 *   sticky 와 충돌하지 않는다 — sticky 는 위에 콘텐츠가 있으면 제자리에 있다가
 *   스크롤이 닿을 때 고정된다. 즉 배너는 스크롤과 함께 올라가 사라지고 바만 남는다.
 */
export default function AppContextBar({ pageLabel, crumbs, left, trailing, children }: { pageLabel?: string; crumbs?: Crumb[]; left?: React.ReactNode; trailing?: React.ReactNode; children?: React.ReactNode }) {
  const { language, toggleLanguage, t } = useLanguage();
  const { email } = useAuth();
  return (
    <>
    <AnnouncementBanner />
    <div className="app-context-bar" style={contextBarStyle}>
      {left !== undefined ? left : crumbs ? (
        <nav aria-label={t("경로", "Breadcrumb")} style={crumbNavStyle} data-crm-i18n-skip="true">
          {crumbs.map((c) => (typeof c === "string" ? { label: c } : c)).filter((c) => c.label).map((c, i) => (
            <React.Fragment key={`${c.label}-${i}`}>
              {i > 0 ? <span aria-hidden="true" style={{ color: "var(--color-text-disabled, #929292)" }}>/</span> : null}
              {/* 경로 칸은 전부 상위 단계라(페이지 이름은 안 넣는다) 갈 곳이 있으면 링크 칩으로 — 호버 시 면·글자색이 바뀐다(crmUI.css .app-crumb). */}
              {c.to ? (
                <Link to={c.to} className="app-crumb app-crumb--link" title={c.label}>{c.label}</Link>
              ) : (
                <span className="app-crumb">{c.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      ) : <div style={labelStyle}>{pageLabel ?? ""}</div>}
      {/* 오른쪽: 피드백 · 도움말 · 알림(같은 32px 고스트, topNavStyle.css .app-context-bar__btn) | 언어 */}
      <div className="app-context-bar__actions" style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, flexShrink: 0 }}>
        <FeedbackWidget />
        {children}
        {email ? <NotificationBell /> : null}
        <span className="app-context-bar__sep" aria-hidden="true" />
        <button
          type="button"
          role="switch"
          aria-checked={language === "en"}
          onClick={toggleLanguage}
          title={t({ ko: "한국어/영어 전환", en: "Switch Korean/English" })}
          className="app-context-bar__lang"
        >
          <span className={language === "ko" ? "is-on" : undefined}>KO</span>
          <span className={language === "en" ? "is-on" : undefined}>EN</span>
        </button>
        {trailing}
      </div>
    </div>
    </>
  );
}

const contextBarStyle: React.CSSProperties = { position: "sticky", top: 0, zIndex: 6, height: 48, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "0 16px", background: "var(--crm-surface-1)", borderBottom: "1px solid var(--crm-border)" };
const crumbNavStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 4, minWidth: 0, fontSize: 13, color: "var(--color-text-description, #767676)" };
const labelStyle: React.CSSProperties = { minWidth: 0, fontSize: 14, fontWeight: 700, color: "var(--crm-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/** 상단 바 액션 버튼 클래스(도움말 등) — 32px 고스트 · hover surface-hover. 정의: topNavStyle.css. */
export const contextActionBtnClass = "app-context-bar__btn";
